import {ProjectMinRes} from '../tracker/project-tracker';
import {Type} from 'class-transformer';
import {
    ClassDefaultSectionModes,
    ClassFormSectionTitle,
    ColumnType,
    DetailGridIgnore,
    DetailGridProperties,
    DisplayName,
    FileUploadRes,
    RequestOverride,
    TableColumn,
    TableDisplayFn,
    TableIgnore,
    TableIndex,
    yascAutocomplete,
    YascEntityName,
    yascFieldKind,
    yascFieldRows,
    yascFormIgnore,
    yascFormIndex,
    yascLevelOfDetail,
    yascMaxLength,
    yascMin,
    yascMinLength,
    yascOptionsKey,
    yascRequired,
} from '@yasc/yasc-material-components';
import {ContactPersonDetailRes} from '../user.contract';
import {formatFromMinutes, minToHours} from './general-task-utils';
import {TaskEffortRes, TaskEffortUpdateReq} from './task-effort';

@ClassFormSectionTitle('crm_task_details_sectionTitle')
@ClassDefaultSectionModes(['create', 'edit', 'copy'])
class baseTask {
    @DisplayName('general_name')
    @TableIndex(0)
    @yascLevelOfDetail(0)
    @DetailGridProperties({row: 0, width: 3})
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(0)
    name!: string;

    @DisplayName('crm_task_taskId_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    taskID?: string;

    @DisplayName('crm_contract_category_label')
    @TableIndex(2)
    @yascLevelOfDetail(1)
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(1)
    @yascAutocomplete()
    @yascOptionsKey('categories')
    category!: string;

    @DisplayName('crm_task_mockup_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    mockups: FileUploadRes[] = [];

    @DisplayName('crm_contract_taskRisks_label')
    @TableColumn(ColumnType.Tag)
    @TableDisplayFn((val: string[]) => val.join(', '))
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    risks: string[] = [];

    @DisplayName('crm_contract_taskClarifications_label')
    @TableColumn(ColumnType.Tag)
    @TableDisplayFn((val: string[]) => val.join(', '))
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    clarifications: string[] = [];

    @DisplayName('crm_contract_taskBudget_label')
    @TableColumn(ColumnType.Number)
    @yascLevelOfDetail(1)
    @yascMin(0)
    @yascFormIgnore()
    maxBudget!: number;

    @DisplayName('crm_task_currentPlanned_label')
    @TableColumn(ColumnType.Number)
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    currentPlanned!: number;

    @DisplayName('general_description')
    @yascLevelOfDetail(2)
    @yascFieldKind('textarea')
    @yascFieldRows(4)
    @DetailGridProperties({fullRow: true})
    @yascFormIndex(10)
    description?: string;

    @DisplayName('crm_task_recentAppointments_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    recentAppointmentsCnt!: number;

    constructor(value?: Partial<baseTask>) {
        Object.assign(this, value)
    }
}

// @ClassDisplayName('crm_contract_baseTaskClass_title')
// @ClassFormSectionTitle('crm_task_details_sectionTitle')
@YascEntityName('crm_task_entityName')
export class BaseTaskRes {
    @DisplayName('crm_task_id_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    id!: string;

    @DisplayName('crm_task_taskId_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    taskId!: string;

    @DisplayName('crm_contract_category_label')
    @TableIndex(2)
    @TableColumn(ColumnType.Text)
    @yascLevelOfDetail(1)
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(1)
    category!: string;

    @DisplayName('crm_task_mockup_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    mockup: string[] = [];

    @DisplayName('crm_task_contact_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    contact: ContactPersonDetailRes[] = [];

    @DisplayName('crm_task_references_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    references: BaseTaskRes[] = [];

    @DisplayName('crm_contract_taskRisks_label')
    @TableColumn(ColumnType.Tag)
    @TableDisplayFn((val: string[]) => val.join(', '))
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    risks: string[] = [];

    @DisplayName('crm_contract_taskClarifications_label')
    @TableColumn(ColumnType.Tag)
    @TableDisplayFn((val: string[]) => val.join(', '))
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    clarifications: string[] = [];

    @DisplayName('crm_task_originalStart_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    originalStart!: string;

    @DisplayName('crm_task_originalEnd_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    originalEnd!: string;

    @DisplayName('crm_task_originalEstimated_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    originalEstimated!: number;

    @DisplayName('crm_task_currentEst_label')
    @TableColumn(ColumnType.Number)
    @yascLevelOfDetail(2)
    @yascMin(0)
    @yascFormIndex(2)
    currentEstimated!: number;

    @DisplayName('crm_task_currentStart_label')
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(3)
    currentStart!: Date;

    @DisplayName('crm_task_currentEnd_label')
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(4)
    currentEnd!: Date;


    @DisplayName('crm_task_currentPlanned_label')
    @TableColumn(ColumnType.Number)
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    currentPlanned!: number;

    @DisplayName('crm_task_taskType_label')
    @TableColumn(ColumnType.Text)
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    taskType!: string;

    @DisplayName('general_name')
    @TableIndex(0)
    @yascLevelOfDetail(0)
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(0)
    name?: string;

    @DisplayName('general_description')
    @yascLevelOfDetail(2)
    @yascFieldKind('textarea')
    @yascFieldRows(4)
    @DetailGridProperties({fullRow: true})
    @yascFormIndex(10)
    description?: string;

    @DisplayName('crm_task_recentAppointments_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    recentAppointmentsCnt!: number;

    @Type(() => ProjectMinRes)
    @DisplayName('general_project')
    @RequestOverride('projectId')
    @TableDisplayFn((val: ProjectMinRes) => val === undefined ? 'No Project Assigned' : val.name)
    @yascLevelOfDetail(3)
    @yascFormIndex(5)
    project?: ProjectMinRes;

    @Type(() => ContactPersonDetailRes)
    @DisplayName('crm_contract_connectedContacts_label')
    @TableDisplayFn((val: ContactPersonDetailRes[]) => val.map(x => x.firstName.charAt(0) + '. ' + x.lastName).join(', '))
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    contacts: ContactPersonDetailRes[] = [];

    @DisplayName('crm_contract_taskCurrentStart_label')
    @DetailGridProperties({row: 3, width: 2})
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    get currentStartDate(): Date {
        return new Date(this.currentStart);
    }

    set currentStartDate(date: Date) {
        this.currentEnd = date
    }

    @DisplayName('crm_contract_taskCurrentEnd_label')
    @DetailGridProperties({row: 3, width: 2})
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    get currentEndDate(): Date {
        return new Date(this.currentEnd)
    }

    set currentEndDate(date: Date) {
        this.currentEnd = date
    }

    @DisplayName('crm_contract_referencedTasks_label')
    @TableIgnore(true)
    @DetailGridProperties({row: 5, width: 3})
    @TableColumn(ColumnType.Select)
    @RequestOverride('referencedTaskIds')
    @Type(() => BaseTaskRes)
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    referencedTasks: BaseTaskRes[] = [];

    @DisplayName('crm_contract_referencedByTasks_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    @TableColumn(ColumnType.Select)
    @RequestOverride('referencedByTaskIds')
    @Type(() => BaseTaskRes)
    @yascLevelOfDetail(2)
    referencedByTask: BaseTaskRes[] = [];

    @DisplayName('crm_contract_taskEffort_label')
    @Type(() => TaskEffortRes)
    @TableDisplayFn((val: TaskEffortRes) => val.name)
    @yascLevelOfDetail(2)
    @DetailGridIgnore()
    efforts: TaskEffortRes[] = [];

    @DisplayName('crm_contract_customerFacingId_label')
    @TableIndex(1)
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    get customerFacingId(): string {
        if (this.taskId !== undefined) return this.taskId;
        return this.id.toString()
    }

    @DisplayName('crm_contract_taskEstimatedEffortSum_label')
    @DetailGridIgnore()
    @TableDisplayFn((val) => val.formatted)
    @yascLevelOfDetail(2)
    get estimatedEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.estimatedDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    @DisplayName('crm_contract_taskActualEffortSum_label')
    @TableDisplayFn((val) => val.formatted)
    @DetailGridIgnore()
    @yascLevelOfDetail(2)
    get actualEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.actualDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    private toUpdateCreate(value: Partial<BaseTaskRes>): BaseTaskCreateReq {
        let create = new BaseTaskCreateReq({})
        create.start = value.currentStart!
        create.end = value.currentEnd!
        create.estimated = value.currentEstimated ?? 0
        create.contactIds = value.contacts?.map(x => x.id) ?? []
        create.referencedTaskIds = value.referencedTasks?.map(x => x.id) ?? []
        create.referencedByTaskIds = value.referencedByTask?.map(x => x.id) ?? []
        create.projectId = value.project?.id
        return create
    }

    public toCreate(): BaseTaskCreateReq {
        return this.toUpdateCreate(this)
    }

    public toUpdate(): BaseTaskUpdateReq {
        let update = this.toUpdateCreate(this) as BaseTaskUpdateReq;
        return update
    }
}

export class BaseTaskCreateReq extends baseTask {
    @DisplayName('general_project')
    @TableIgnore(true)
    @yascAutocomplete()
    @yascOptionsKey('projects')
    @yascFormIndex(5)
    projectId?: number;

    @DisplayName('crm_contract_connectedContacts_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    contactIds: number[] = [];

    @DisplayName('crm_contract_referencedTasks_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    referencedTaskIds: string[] = [];

    @DisplayName('crm_contract_referencedByTasks_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    referencedByTaskIds: string[] = [];

    @DisplayName('crm_task_currentEst_label')
    @TableColumn(ColumnType.Number)
    @yascLevelOfDetail(2)
    @yascMin(0)
    @yascFormIndex(4)
    estimated!: number;

    @DisplayName('crm_task_currentStart_label')
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(2)
    start!: Date;

    @DisplayName('crm_task_currentEnd_label')
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(3)
    end!: Date;

    constructor(value: Partial<BaseTaskCreateReq>) {
        super(value);
        Object.assign(this, value)
    }
}

export class BaseTaskUpdateReq extends BaseTaskCreateReq {
    @DisplayName('crm_task_id_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    id!: string;

    @DisplayName('crm_contract_taskEffort_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    efforts: TaskEffortUpdateReq[] = [];

    constructor(value: Partial<BaseTaskUpdateReq>) {
        super(value);
        Object.assign(this, value)
    }

    @DisplayName('crm_contract_taskEstimatedEffortSum_label')
    @DetailGridIgnore()
    @TableDisplayFn((val) => val.formatted)
    @yascLevelOfDetail(1)
    get estimatedEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.estimatedDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    @DisplayName('crm_contract_taskActualEffortSum_label')
    @TableDisplayFn((val) => val.formatted)
    @DetailGridIgnore()
    @yascLevelOfDetail(1)
    get actualEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.actualDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }
}
